import { useEffect, useMemo, useRef, useState } from 'react';
import UkDateField from '@/components/UkDateField';
import { supabase } from '@/integrations/supabase/client';
import {
  isGoogleCalendarConfigured,
} from '@/lib/google-calendar';
import {
  isGoogleDriveConfigured,
} from '@/lib/google-drive';
import { normalizeHomePropertyLookupInput } from '@/lib/home-property-enrichment';
import {
  loadIntegrationSettings,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';
import {
  updateSetupCalendarMode,
  updateSetupDocumentsMode,
} from '@/lib/integration-setup-actions';
import { invokeDvlaVehicleLookup } from '@/lib/dvla';
import { saveWebOnboardingCompleted } from '@/lib/webOnboardingProgress';
import type { DependantType, WorkType } from '@/types';
import { toast } from 'sonner';
import {
  AppButton,
  AppInput,
  AppNativeSelect,
  AppPanelButton,
  AppTextLinkButton,
  normalizeVehicleRegistration,
  type SharedDvlaVehicleLookupResult,
} from '../../ui-foundation';
import { calculateAgeFromDateOfBirth } from '../../ui-foundation/features/family';
import { HEALTH_PET_TYPE_OPTIONS, formatPetTypeLabel } from '../../ui-foundation/features/health';
import {
  MANUAL_FINANCE_SETUP_BODY,
  MANUAL_FINANCE_SETUP_NOTE,
  MANUAL_FINANCE_SETUP_SUCCESS,
  MANUAL_FINANCE_SETUP_TITLE,
  buildManualFinanceSetupAnnualIncome,
  buildManualFinanceSetupBillRows,
  buildManualFinanceSetupDraft,
  buildManualFinanceSetupHomeProfilePatch,
  getManualFinanceHousingCostLabel,
  getManualFinanceHousingProviderLabel,
  hasManualFinanceSetupInput,
  type ManualFinanceSetupDraft,
} from '../../ui-foundation/features/money';
import {
  ONBOARDING_COUNCIL_TAX_BANDS,
  ONBOARDING_COMPLETE_TITLE,
  ONBOARDING_DATA_PROMISE,
  ONBOARDING_DEPENDANT_OPTIONS,
  ONBOARDING_CONNECTIONS_NOTE,
  ONBOARDING_HOME_ROUTINES_NOTE,
  ONBOARDING_INTEGRATION_INTENT_OPTIONS,
  ONBOARDING_STEP_COPY,
  ONBOARDING_STEP_SEQUENCE,
  ONBOARDING_STEP_SUPPORT_NOTE,
  ONBOARDING_TENURE_OPTIONS,
  ONBOARDING_WELCOME_BODY_PRIMARY,
  ONBOARDING_WELCOME_BODY_SECONDARY,
  ONBOARDING_WELCOME_TITLE,
  ONBOARDING_WORK_OPTIONS,
  SETUP_CHOICES_SUBTITLE,
  SETUP_CHOICES_TITLE,
  SETUP_COMPLETE_SUMMARY_BODY,
  SETUP_COMPLETE_SUMMARY_FOLLOW_UP,
  SETUP_FINANCE_HANDOFF_BODY,
  SETUP_FINANCE_HANDOFF_NOTE,
  SETUP_FINANCE_HANDOFF_TITLE,
  SETUP_NEXT_STEP_BODY,
  SETUP_NEXT_STEP_PRIMARY_LABEL,
  SETUP_NEXT_STEP_SECONDARY_LABEL,
  SETUP_NEXT_STEP_TITLE,
  SETUP_OPTIONAL_EXTRAS_BODY,
  SETUP_OPTIONAL_EXTRAS_TITLE,
  createOnboardingAdultDependantDraft,
  createOnboardingChildDraft,
  createOnboardingPetDraft,
  type OnboardingAdultDependantDraft,
  type OnboardingIntegrationIntent,
  type OnboardingPetDraft,
  type OnboardingYesNo,
} from '../../ui-foundation/features/onboarding';
import {
  collectSelectedSchoolNames,
  type FamilySchoolLookupCandidate,
  isChildSchoolSelectionRelevant,
  resolveSchoolLookupPostcode,
} from '../../ui-foundation/features/family';
import { describeFinanceSetupChoice, describeSetupChoice } from '../../ui-foundation/features/onboarding';

type SetupStep = 1 | 2 | 3 | 4 | 5;

function normalizeName(value: string) {
  return value.trim().toLowerCase();
}

function relationshipLabel(kind: Exclude<DependantType, 'children'>) {
  return kind === 'elderly' ? 'Older family member' : 'Disabled or unwell family member';
}

async function upsertPartnerRow(userId: string, name: string, dateOfBirth: string) {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return;
  }

  const { data: existingRows, error: loadError } = await supabase
    .from('adult_dependants')
    .select('id, name, relationship')
    .eq('user_id', userId);

  if (loadError) {
    throw loadError;
  }

  const partnerRow = (existingRows || []).find((row: any) => (row.relationship || '').toLowerCase() === 'partner');
  const payload = {
    user_id: userId,
    name: trimmedName,
    relationship: 'Partner',
    date_of_birth: dateOfBirth || null,
  } as const;

  if (partnerRow?.id) {
    const { error } = await supabase.from('adult_dependants').update(payload).eq('id', partnerRow.id);
    if (error) {
      throw error;
    }
    return;
  }

  const { error } = await supabase.from('adult_dependants').insert(payload);
  if (error) {
    throw error;
  }
}

async function mergeChildRows(
  userId: string,
  childDrafts: Array<{ name: string; dateOfBirth: string; schoolName: string; schoolUrn: string }>,
) {
  const nextChildren = childDrafts
    .map((child) => ({
      name: child.name.trim(),
      dateOfBirth: child.dateOfBirth,
      schoolName: child.schoolName.trim(),
      schoolUrn: child.schoolUrn.trim(),
    }))
    .filter((child) => child.name || child.dateOfBirth);

  if (!nextChildren.length) {
    return;
  }

  const { data: existingRows, error: loadError } = await supabase
    .from('children')
    .select('id, name, age')
    .eq('user_id', userId);

  if (loadError) {
    throw loadError;
  }

  for (const child of nextChildren) {
    const existing = (existingRows || []).find((row: any) => normalizeName(row.name || '') === normalizeName(child.name));
    const payload = {
      user_id: userId,
      name: child.name || 'Child',
      age: calculateAgeFromDateOfBirth(child.dateOfBirth) ?? existing?.age ?? 0,
      date_of_birth: child.dateOfBirth || null,
      school_name: child.schoolName || null,
      school_urn: child.schoolUrn || null,
    };

    if (existing?.id) {
      const { error } = await supabase.from('children').update(payload).eq('id', existing.id);
      if (error) {
        throw error;
      }
    } else {
      const { error } = await supabase.from('children').insert(payload);
      if (error) {
        throw error;
      }
    }
  }
}

async function mergeAdultDependantRows(
  userId: string,
  dependantDrafts: OnboardingAdultDependantDraft[],
) {
  const nextDependants = dependantDrafts
    .map((dependant) => ({
      name: dependant.name.trim(),
      dateOfBirth: dependant.dateOfBirth,
      kind: dependant.kind,
    }))
    .filter((dependant) => dependant.name || dependant.dateOfBirth);

  if (!nextDependants.length) {
    return;
  }

  const { data: existingRows, error: loadError } = await supabase
    .from('adult_dependants')
    .select('id, name, relationship')
    .eq('user_id', userId);

  if (loadError) {
    throw loadError;
  }

  for (const dependant of nextDependants) {
    const relationship = relationshipLabel(dependant.kind);
    const existing = (existingRows || []).find((row: any) => (
      normalizeName(row.name || '') === normalizeName(dependant.name)
      && (row.relationship || '') !== 'Partner'
    ));

    const payload = {
      user_id: userId,
      name: dependant.name || relationship,
      relationship,
      date_of_birth: dependant.dateOfBirth || null,
    };

    if (existing?.id) {
      const { error } = await supabase.from('adult_dependants').update(payload).eq('id', existing.id);
      if (error) {
        throw error;
      }
    } else {
      const { error } = await supabase.from('adult_dependants').insert(payload);
      if (error) {
        throw error;
      }
    }
  }
}

async function mergePetRows(userId: string, petDrafts: OnboardingPetDraft[]) {
  const nextPets = petDrafts
    .map((pet) => ({
      name: pet.name.trim(),
      petType: pet.petType.trim(),
      dateOfBirth: pet.dateOfBirth,
    }))
    .filter((pet) => pet.name || pet.dateOfBirth);

  if (!nextPets.length) {
    return;
  }

  const { data: existingRows, error: loadError } = await supabase
    .from('pets')
    .select('id, name, pet_type')
    .eq('user_id', userId);

  if (loadError) {
    throw loadError;
  }

  for (const pet of nextPets) {
    const existing = (existingRows || []).find((row: any) => (
      normalizeName(row.name || '') === normalizeName(pet.name)
      && (row.pet_type || '') === pet.petType
    ));

    const payload = {
      user_id: userId,
      name: pet.name || 'Pet',
      pet_type: pet.petType || 'dog',
      dob: pet.dateOfBirth || null,
    };

    if (existing?.id) {
      const { error } = await supabase.from('pets').update(payload).eq('id', existing.id);
      if (error) {
        throw error;
      }
    } else {
      const { error } = await supabase.from('pets').insert(payload);
      if (error) {
        throw error;
      }
    }
  }
}

interface WelcomeScreenProps {
  userId: string;
  onComplete: () => void;
}

export default function WelcomeScreen({ userId, onComplete }: WelcomeScreenProps) {
  const [step, setStep] = useState<SetupStep>(1);
  const [firstName, setFirstName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [workType, setWorkType] = useState<WorkType>('');
  const [partnerChoice, setPartnerChoice] = useState<OnboardingYesNo>('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerDateOfBirth, setPartnerDateOfBirth] = useState('');
  const [dependantTypes, setDependantTypes] = useState<DependantType[]>([]);
  const [childDrafts, setChildDrafts] = useState([createOnboardingChildDraft()]);
  const [adultDependantDrafts, setAdultDependantDrafts] = useState<OnboardingAdultDependantDraft[]>([]);
  const [hasPets, setHasPets] = useState<OnboardingYesNo>('');
  const [petDrafts, setPetDrafts] = useState<OnboardingPetDraft[]>([createOnboardingPetDraft()]);
  const [address, setAddress] = useState('');
  const [tenure, setTenure] = useState('');
  const [councilTaxBand, setCouncilTaxBand] = useState('');
  const [hasLawn, setHasLawn] = useState<OnboardingYesNo>('');
  const [hasHouseplants, setHasHouseplants] = useState<OnboardingYesNo>('');
  const [hasVehicle, setHasVehicle] = useState<OnboardingYesNo>('');
  const [vehicleRegistrationNumber, setVehicleRegistrationNumber] = useState('');
  const [bankIntent, setBankIntent] = useState<OnboardingIntegrationIntent>('add-later');
  const [storageIntent, setStorageIntent] = useState<OnboardingIntegrationIntent>('add-later');
  const [calendarIntent, setCalendarIntent] = useState<OnboardingIntegrationIntent>('add-later');
  const [finishing, setFinishing] = useState(false);
  const [setupSaved, setSetupSaved] = useState(false);
  const [manualFinanceDraft, setManualFinanceDraft] = useState<ManualFinanceSetupDraft>(() => buildManualFinanceSetupDraft({}));
  const [manualFinanceSaving, setManualFinanceSaving] = useState(false);
  const [manualFinanceSaved, setManualFinanceSaved] = useState(false);
  const [integrationSetup, setIntegrationSetup] = useState<IntegrationSettingsRecord | null>(null);
  const [documentsModeSaving, setDocumentsModeSaving] = useState(false);
  const [calendarModeSaving, setCalendarModeSaving] = useState(false);
  const [childSchoolCandidates, setChildSchoolCandidates] = useState<FamilySchoolLookupCandidate[]>([]);
  const [childSchoolLookupBusy, setChildSchoolLookupBusy] = useState(false);
  const [childSchoolLookupError, setChildSchoolLookupError] = useState<string | null>(null);
  const [schoolCalendarImportBusy, setSchoolCalendarImportBusy] = useState(false);
  const childSchoolLookupAutoKeyRef = useRef('');

  useEffect(() => {
    let active = true;

    const loadExistingSetup = async () => {
      try {
        const [profileResult, homeResult] = await Promise.all([
          supabase
            .from('profiles')
            .select('first_name, date_of_birth, work_type, partner_name, partner_date_of_birth, dependant_types')
            .eq('user_id', userId)
            .maybeSingle(),
          supabase
            .from('home_profiles')
            .select('address, tenure, council_tax_band, has_lawn, has_houseplants, has_vehicle, vehicle_registration_number')
            .eq('user_id', userId)
            .maybeSingle(),
        ]);

        if (!active) {
          return;
        }

        if (profileResult.data) {
          setFirstName(profileResult.data.first_name || '');
          setDateOfBirth(profileResult.data.date_of_birth || '');
          setWorkType((profileResult.data.work_type || '') as WorkType);
          setPartnerName(profileResult.data.partner_name || '');
          setPartnerDateOfBirth(profileResult.data.partner_date_of_birth || '');
          setPartnerChoice(profileResult.data.partner_name ? 'yes' : '');
          setDependantTypes(((profileResult.data.dependant_types || []) as DependantType[]).filter(Boolean));
        }

        if (homeResult.data) {
          setAddress(homeResult.data.address || '');
          setTenure(homeResult.data.tenure || '');
          setCouncilTaxBand(homeResult.data.council_tax_band || '');
          setHasLawn(homeResult.data.has_lawn === null ? '' : homeResult.data.has_lawn ? 'yes' : 'no');
          setHasHouseplants(homeResult.data.has_houseplants === null ? '' : homeResult.data.has_houseplants ? 'yes' : 'no');
          setHasVehicle(homeResult.data.has_vehicle === null ? '' : homeResult.data.has_vehicle ? 'yes' : 'no');
          setVehicleRegistrationNumber(homeResult.data.vehicle_registration_number || '');
        }
      } catch (error) {
        console.error(error);
      }
    };

    void loadExistingSetup();

    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    let active = true;

    if (!setupSaved) {
      return () => {
        active = false;
      };
    }

    void loadIntegrationSettings(userId)
      .then((settings) => {
        if (active) {
          setIntegrationSetup(settings);
        }
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      active = false;
    };
  }, [setupSaved, userId]);

  useEffect(() => {
    if (!setupSaved || bankIntent !== 'connect-now') {
      return;
    }

    setManualFinanceDraft(buildManualFinanceSetupDraft({ tenure }));
    setManualFinanceSaved(false);
  }, [bankIntent, setupSaved, tenure]);

  const validChildNames = useMemo(
    () => childDrafts.map((child) => child.name.trim()).filter(Boolean),
    [childDrafts],
  );
  const schoolEligibleChildren = useMemo(
    () => childDrafts.filter((child) => isChildSchoolSelectionRelevant({ dateOfBirth: child.dateOfBirth })),
    [childDrafts],
  );
  const selectedSchoolNames = useMemo(
    () => collectSelectedSchoolNames(schoolEligibleChildren),
    [schoolEligibleChildren],
  );
  const childSchoolLookupPostcode = useMemo(
    () => resolveSchoolLookupPostcode({ address }),
    [address],
  );
  const childSchoolLookupAutoKey = schoolEligibleChildren.length && childSchoolLookupPostcode
    ? childSchoolLookupPostcode
    : '';

  const canAdvance = {
    1: Boolean(firstName.trim() && workType),
    2: partnerChoice !== '',
    3: hasPets !== '',
    4: hasVehicle !== '',
    5: Boolean(bankIntent && storageIntent && calendarIntent),
  } satisfies Record<SetupStep, boolean>;

  const toggleDependant = (value: DependantType) => {
    setDependantTypes((current) => {
      const exists = current.includes(value);
      const next = exists ? current.filter((item) => item !== value) : [...current, value];

      if (!exists && value === 'children' && childDrafts.length === 0) {
        setChildDrafts([createOnboardingChildDraft()]);
      }

      if (!exists && value !== 'children' && !adultDependantDrafts.some((draft) => draft.kind === value)) {
        setAdultDependantDrafts((existingDrafts) => [...existingDrafts, createOnboardingAdultDependantDraft(value)]);
      }

      return next;
    });
  };

  const setChildDraft = (id: string, field: 'name' | 'dateOfBirth', value: string) => {
    setChildDrafts((current) => current.map((child) => {
      if (child.id !== id) return child;
      const nextChild = { ...child, [field]: value };
      if (field === 'dateOfBirth' && !isChildSchoolSelectionRelevant({ dateOfBirth: value })) {
        nextChild.schoolName = '';
        nextChild.schoolUrn = '';
      }
      return nextChild;
    }));
  };

  const assignChildSchool = (id: string, schoolUrn: string) => {
    const match = childSchoolCandidates.find((candidate) => candidate.urn === schoolUrn);
    setChildDrafts((current) => current.map((child) => (
      child.id === id
        ? {
            ...child,
            schoolName: match?.name || '',
            schoolUrn: match?.urn || '',
          }
        : child
    )));
  };

  const addChildDraft = () => {
    setDependantTypes((current) => current.includes('children') ? current : [...current, 'children']);
    setChildDrafts((current) => [...current, createOnboardingChildDraft()]);
  };

  const removeChildDraft = (id: string) => {
    setChildDrafts((current) => {
      const next = current.filter((child) => child.id !== id);
      return next.length ? next : [createOnboardingChildDraft()];
    });
  };

  const setAdultDependantDraft = (
    id: string,
    field: 'name' | 'dateOfBirth' | 'kind',
    value: string,
  ) => {
    setAdultDependantDrafts((current) => current.map((dependant) => (
      dependant.id === id
        ? {
            ...dependant,
            [field]: field === 'kind' ? value as OnboardingAdultDependantDraft['kind'] : value,
          }
        : dependant
    )));
  };

  const addAdultDependantDraft = () => {
    const defaultKind = dependantTypes.includes('elderly')
      ? 'elderly'
      : dependantTypes.includes('disabled')
        ? 'disabled'
        : 'elderly';
    setDependantTypes((current) => {
      const next = [...current];
      if (!next.includes(defaultKind)) {
        next.push(defaultKind);
      }
      return next;
    });
    setAdultDependantDrafts((current) => [...current, createOnboardingAdultDependantDraft(defaultKind)]);
  };

  const removeAdultDependantDraft = (id: string) => {
    setAdultDependantDrafts((current) => current.filter((dependant) => dependant.id !== id));
  };

  const setPetDraft = (id: string, field: keyof OnboardingPetDraft, value: string) => {
    setPetDrafts((current) => current.map((pet) => pet.id === id ? { ...pet, [field]: value } : pet));
  };

  const addPetDraft = () => {
    setHasPets('yes');
    setPetDrafts((current) => [...current, createOnboardingPetDraft()]);
  };

  const removePetDraft = (id: string) => {
    setPetDrafts((current) => {
      const next = current.filter((pet) => pet.id !== id);
      return next.length ? next : [createOnboardingPetDraft()];
    });
  };

  const loadLatestIntegrationSettings = async () => {
    const nextSettings = await loadIntegrationSettings(userId);
    setIntegrationSetup(nextSettings);
    return nextSettings;
  };

  const handleDocumentsModeChange = async (mode: 'adulting' | 'google_drive') => {
    if (documentsModeSaving) {
      return;
    }

    setDocumentsModeSaving(true);
    try {
      const result = await updateSetupDocumentsMode({
        userId,
        mode,
        childNames: validChildNames,
      });

      if (result.settings) {
        setIntegrationSetup(result.settings);
      }

      if (result.notice?.tone === 'info') {
        toast.info(result.notice.message);
        return;
      }

      if (result.notice) {
        toast.success(result.notice.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not update document storage.');
    } finally {
      setDocumentsModeSaving(false);
    }
  };

  const handleCalendarModeChange = async (mode: 'adulting' | 'google_calendar') => {
    if (calendarModeSaving) {
      return;
    }

    setCalendarModeSaving(true);
    try {
      const result = await updateSetupCalendarMode({
        userId,
        mode,
        currentSettings: integrationSetup || await loadLatestIntegrationSettings(),
      });

      if (result.settings) {
        setIntegrationSetup(result.settings);
      }

      if (result.notice?.tone === 'info') {
        toast.info(result.notice.message);
        return;
      }

      if (result.notice) {
        toast.success(result.notice.message);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not update calendar mode.');
    } finally {
      setCalendarModeSaving(false);
    }
  };

  const handleLookupChildSchools = async () => {
    if (!childSchoolLookupPostcode) {
      setChildSchoolLookupError('Add a full home address with postcode first so Adulting can find nearby schools.');
      return;
    }

    setChildSchoolLookupBusy(true);
    setChildSchoolLookupError(null);

    try {
      const { data, error } = await supabase.functions.invoke('family-school-calendar-import', {
        body: { postcode: childSchoolLookupPostcode, mode: 'lookup' },
      });

      if (error) {
        throw error;
      }

      if ((data as any)?.error) {
        throw new Error(String((data as any).error));
      }

      const schools = Array.isArray((data as any)?.schools)
        ? ((data as any).schools as FamilySchoolLookupCandidate[])
        : [];
      setChildSchoolCandidates(schools);

      if (!schools.length) {
        setChildSchoolLookupError('No nearby schools came back yet. You can still add the school name later in My Settings.');
      }
    } catch (error) {
      console.error(error);
      setChildSchoolLookupError(error instanceof Error ? error.message : 'Could not look up nearby schools.');
    } finally {
      setChildSchoolLookupBusy(false);
    }
  };

  const handleImportSchoolDates = async () => {
    if (!selectedSchoolNames.length) {
      return;
    }

    if (!childSchoolLookupPostcode) {
      toast.error('Add a full home address with postcode first so Adulting can import school dates.');
      return;
    }

    setSchoolCalendarImportBusy(true);

    try {
      const { data, error } = await supabase.functions.invoke('family-school-calendar-import', {
        body: {
          postcode: childSchoolLookupPostcode,
          selectedSchoolNames,
        },
      });

      if (error) {
        throw error;
      }

      if ((data as any)?.error) {
        throw new Error(String((data as any).error));
      }

      toast.success(
        selectedSchoolNames.length === 1
          ? `Imported school dates for ${selectedSchoolNames[0]}.`
          : `Imported school dates for ${selectedSchoolNames.join(', ')}.`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not import school dates.');
    } finally {
      setSchoolCalendarImportBusy(false);
    }
  };

  useEffect(() => {
    if (!schoolEligibleChildren.length) {
      if (childSchoolLookupAutoKeyRef.current) {
        childSchoolLookupAutoKeyRef.current = '';
      }
      if (childSchoolCandidates.length) {
        setChildSchoolCandidates([]);
      }
      if (childSchoolLookupError) {
        setChildSchoolLookupError(null);
      }
      return;
    }

    if (!childSchoolLookupAutoKey) {
      if (childSchoolLookupAutoKeyRef.current) {
        childSchoolLookupAutoKeyRef.current = '';
      }
      if (childSchoolCandidates.length) {
        setChildSchoolCandidates([]);
      }
      if (childSchoolLookupError) {
        setChildSchoolLookupError(null);
      }
      return;
    }

    if (childSchoolLookupBusy || childSchoolLookupAutoKeyRef.current === childSchoolLookupAutoKey) {
      return;
    }

    childSchoolLookupAutoKeyRef.current = childSchoolLookupAutoKey;
    void handleLookupChildSchools();
  }, [childSchoolCandidates.length, childSchoolLookupAutoKey, childSchoolLookupBusy, childSchoolLookupError, schoolEligibleChildren.length]);

  const handleFinish = async () => {
    setFinishing(true);

    try {
      const hasNamedChildren = childDrafts.some((child) => child.name.trim().length > 0);
      const nextDependantTypes = Array.from(new Set([
        ...dependantTypes,
        ...(hasNamedChildren ? (['children'] as const) : []),
      ]));
      const propertyLookup = normalizeHomePropertyLookupInput(address);
      const normalizedRegistration = normalizeVehicleRegistration(vehicleRegistrationNumber);
      let vehicleLookup: SharedDvlaVehicleLookupResult | null = null;

      if (hasVehicle === 'yes' && normalizedRegistration) {
        const { data, error } = await invokeDvlaVehicleLookup(normalizedRegistration);
        if (error) {
          throw error;
        }
        vehicleLookup = data || null;
      }

      const employmentType = workType === 'mix' ? '' : workType;

      const { error: profileError } = await supabase.from('profiles').upsert({
        user_id: userId,
        first_name: firstName.trim(),
        date_of_birth: dateOfBirth || null,
        work_type: workType,
        employment_type: employmentType,
        partner_name: partnerChoice === 'yes' ? partnerName.trim() : '',
        partner_date_of_birth: partnerChoice === 'yes' ? partnerDateOfBirth || null : null,
        dependant_types: nextDependantTypes,
      } as any, { onConflict: 'user_id' });

      if (profileError) {
        throw profileError;
      }

      if (partnerChoice === 'yes' && partnerName.trim()) {
        await upsertPartnerRow(userId, partnerName, partnerDateOfBirth);
      }

      await mergeChildRows(userId, childDrafts);
      await mergeAdultDependantRows(
        userId,
        adultDependantDrafts.filter((dependant) => dependantTypes.includes(dependant.kind)),
      );

      if (hasPets === 'yes') {
        await mergePetRows(userId, petDrafts);
      }

      if (address.trim() || tenure || councilTaxBand || hasLawn !== '' || hasHouseplants !== '' || hasVehicle !== '') {
        const { data: existingHome, error: loadHomeError } = await supabase
          .from('home_profiles')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        if (loadHomeError) {
          throw loadHomeError;
        }

        const homePayload = {
          user_id: userId,
          address: address.trim() || null,
          tenure: tenure || null,
          council_tax_band: councilTaxBand || null,
          council_tax_match_status: councilTaxBand ? 'matched' : 'not_checked',
          council_tax_value_origin: councilTaxBand ? 'user_entered' : null,
          property_lookup_query: propertyLookup.lookupQuery,
          property_address_normalised: propertyLookup.lookupQuery,
          property_postcode_normalised: propertyLookup.postcode,
          has_lawn: hasLawn === '' ? null : hasLawn === 'yes',
          has_houseplants: hasHouseplants === '' ? null : hasHouseplants === 'yes',
          has_vehicle: hasVehicle === '' ? null : hasVehicle === 'yes',
          vehicle_registration_number: hasVehicle === 'yes' && normalizedRegistration ? normalizedRegistration : null,
          vehicle_make: hasVehicle === 'yes' ? vehicleLookup?.make || null : null,
          vehicle_mot_due_date: hasVehicle === 'yes' ? vehicleLookup?.motExpiryDate || null : null,
          vehicle_mot_status: hasVehicle === 'yes' ? vehicleLookup?.motStatus || null : null,
          vehicle_tax_due_date: hasVehicle === 'yes' ? vehicleLookup?.taxDueDate || null : null,
          vehicle_tax_status: hasVehicle === 'yes' ? vehicleLookup?.taxStatus || null : null,
          vehicle_last_synced_at: hasVehicle === 'yes' ? vehicleLookup?.generatedAt || null : null,
        };

        if (existingHome?.id) {
          const { error } = await supabase.from('home_profiles').update(homePayload as any).eq('id', existingHome.id);
          if (error) {
            throw error;
          }
        } else {
          const { error } = await supabase.from('home_profiles').insert(homePayload as any);
          if (error) {
            throw error;
          }
        }
      }

      localStorage.setItem('adulting_show_work', JSON.stringify(true));
      localStorage.setItem('adulting_show_family', JSON.stringify(nextDependantTypes.length > 0 || Boolean(partnerName.trim())));
      saveWebOnboardingCompleted();

      setSetupSaved(true);
      setIntegrationSetup(await loadLatestIntegrationSettings());
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not finish setup right now.');
    } finally {
      setFinishing(false);
    }
  };

  const handleSkip = () => {
    saveWebOnboardingCompleted();
    onComplete();
  };

  const handleSaveManualFinanceBasics = async () => {
    if (!hasManualFinanceSetupInput(manualFinanceDraft)) {
      toast.error('Add at least one finance basic first.');
      return;
    }

    setManualFinanceSaving(true);

    try {
      const annualIncome = buildManualFinanceSetupAnnualIncome(manualFinanceDraft);
      const homePatch = buildManualFinanceSetupHomeProfilePatch(userId, tenure, manualFinanceDraft);
      const billRows = buildManualFinanceSetupBillRows(userId, tenure, manualFinanceDraft);
      const tasks: Array<Promise<{ error: Error | null }>> = [];

      if (annualIncome != null) {
        tasks.push(
          supabase.from('profiles').upsert(
            {
              user_id: userId,
              annual_income: annualIncome,
            } as any,
            { onConflict: 'user_id' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      if (Object.keys(homePatch).length > 1) {
        tasks.push(
          (supabase.from('home_profiles') as any).upsert(
            homePatch,
            { onConflict: 'user_id' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      if (billRows.length) {
        tasks.push(
          (supabase.from('home_bill_settings' as any) as any).upsert(
            billRows,
            { onConflict: 'user_id,bill_key' },
          ) as Promise<{ error: Error | null }>,
        );
      }

      if (!tasks.length) {
        toast.error('Add at least one finance basic first.');
        setManualFinanceSaving(false);
        return;
      }

      const results = await Promise.all(tasks);
      const error = results.find((result) => result.error)?.error;
      if (error) {
        throw error;
      }

      setManualFinanceSaved(true);
      window.dispatchEvent(new CustomEvent('adulting:data-changed'));
      toast.success(MANUAL_FINANCE_SETUP_SUCCESS);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Could not save finance basics right now.');
    } finally {
      setManualFinanceSaving(false);
    }
  };

  const finishToLifeCheck = () => {
    window.location.assign('/life-check?entry=ignition');
  };

  const finishToProfile = () => {
    window.location.assign('/profile');
  };

  const goBack = () => {
    setStep((current) => current > 1 ? ((current - 1) as SetupStep) : current);
  };

  const goNext = () => {
    setStep((current) => current < 5 ? ((current + 1) as SetupStep) : current);
  };

  if (setupSaved) {
    const googleDriveReady = integrationSetup?.documents_mode === 'google_drive' && isGoogleDriveConfigured(integrationSetup);
    const googleCalendarReady = integrationSetup?.calendar_mode === 'google_calendar' && isGoogleCalendarConfigured(integrationSetup);
    const hasImmediateExtras = bankIntent === 'connect-now' || storageIntent === 'connect-now' || calendarIntent === 'connect-now';
    const setupChoices = [
      describeSetupChoice('Storage', storageIntent),
      describeSetupChoice('Calendar', calendarIntent),
      describeFinanceSetupChoice(bankIntent),
    ];

    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-2xl animate-fade-in space-y-4">
          <div className="rounded-[28px] border border-card-border bg-card px-5 py-6 shadow-sm">
            <img src="/adulting-logo.png" alt="Adulting" className="h-16 mx-auto mb-4" />
            <div className="space-y-4 text-left">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-primary">{ONBOARDING_COMPLETE_TITLE}</h1>
              </div>
              <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
                <p>{SETUP_COMPLETE_SUMMARY_BODY}</p>
                <p>{SETUP_COMPLETE_SUMMARY_FOLLOW_UP}</p>
              </div>
              <div className="rounded-2xl bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
                {ONBOARDING_DATA_PROMISE}
                {' '}
                Full detail lives in the
                {' '}
                <a href="/privacy" className="text-primary underline-offset-4 hover:underline">Privacy policy</a>.
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">{SETUP_NEXT_STEP_TITLE}</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {SETUP_NEXT_STEP_BODY}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <AppButton onPress={finishToLifeCheck} className="w-full">
                {SETUP_NEXT_STEP_PRIMARY_LABEL}
              </AppButton>
              <AppButton tone="outline" onPress={finishToProfile} className="w-full">
                {SETUP_NEXT_STEP_SECONDARY_LABEL}
              </AppButton>
            </div>
          </div>

          {hasImmediateExtras ? (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-2">
              <p className="text-sm font-semibold text-foreground">{SETUP_OPTIONAL_EXTRAS_TITLE}</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{SETUP_OPTIONAL_EXTRAS_BODY}</p>
            </div>
          ) : null}

          {bankIntent === 'connect-now' ? (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">{MANUAL_FINANCE_SETUP_TITLE}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{MANUAL_FINANCE_SETUP_BODY}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{MANUAL_FINANCE_SETUP_NOTE}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{SETUP_FINANCE_HANDOFF_NOTE}</p>
                {manualFinanceSaved ? (
                  <p className="text-xs font-medium text-primary">{MANUAL_FINANCE_SETUP_SUCCESS}</p>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Monthly household income (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.monthlyIncome}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, monthlyIncome: event.target.value }))}
                    placeholder="e.g. 2400"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">{getManualFinanceHousingCostLabel(tenure)}</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.housingMonthlyCost}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, housingMonthlyCost: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">{getManualFinanceHousingProviderLabel(tenure)}</span>
                  <AppInput
                    value={manualFinanceDraft.housingProvider}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, housingProvider: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Council tax monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.councilTaxMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, councilTaxMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Energy provider</span>
                  <AppInput
                    value={manualFinanceDraft.energyProvider}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, energyProvider: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Gas monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.gasMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, gasMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Electricity monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.electricityMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, electricityMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Water monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.waterMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, waterMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Broadband provider</span>
                  <AppInput
                    value={manualFinanceDraft.broadbandProvider}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, broadbandProvider: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Broadband monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.broadbandMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, broadbandMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <UkDateField
                  label="Broadband contract end"
                  value={manualFinanceDraft.broadbandContractEnd}
                  onChange={(value) => setManualFinanceDraft((current) => ({ ...current, broadbandContractEnd: value }))}
                  blankHint="Optional. Add this if you already know when the contract ends."
                />

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-foreground">Mobile provider</span>
                  <AppInput
                    value={manualFinanceDraft.phoneProvider}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, phoneProvider: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>

                <label className="space-y-1 text-sm sm:col-span-2">
                  <span className="font-medium text-foreground">Mobile phone monthly (£)</span>
                  <AppInput
                    type="number"
                    inputMode="decimal"
                    value={manualFinanceDraft.mobilePhoneMonthly}
                    onChange={(event) => setManualFinanceDraft((current) => ({ ...current, mobilePhoneMonthly: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>
              </div>

              <AppButton onPress={() => void handleSaveManualFinanceBasics()} disabled={manualFinanceSaving}>
                {manualFinanceSaving ? 'Saving…' : 'Save finance basics'}
              </AppButton>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">{SETUP_FINANCE_HANDOFF_TITLE}</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{SETUP_FINANCE_HANDOFF_BODY}</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{SETUP_FINANCE_HANDOFF_NOTE}</p>
              <AppButton tone="outline" onPress={finishToProfile} className="w-full">
                Open My Settings
              </AppButton>
            </div>
          )}

          {storageIntent === 'connect-now' ? (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Storage setup</p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                If you’d like, connect your own storage now. Otherwise Adulting can keep documents securely managed in its own cloud and available across your Adulting devices. Apple Files stays part of the iPhone flow.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <AppButton
                  tone={googleDriveReady ? 'primary' : 'outline'}
                  onPress={() => void handleDocumentsModeChange(googleDriveReady ? 'adulting' : 'google_drive')}
                  disabled={documentsModeSaving}
                  className="w-full"
                >
                  {documentsModeSaving ? 'Working…' : googleDriveReady ? 'Use Adulting storage instead' : 'Connect Google Drive'}
                </AppButton>
                <AppButton
                  tone="outline"
                  onPress={() => void handleDocumentsModeChange('adulting')}
                  disabled={documentsModeSaving}
                  className="w-full"
                >
                  Keep Adulting storage
                </AppButton>
              </div>
            </div>
          ) : null}

          {calendarIntent === 'connect-now' ? (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Calendar setup</p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                If you’d like, connect your calendar now. Otherwise Adulting can keep the source calendar calm and organised inside Adulting first, then you can add an external copy later. Apple Calendar stays part of the iPhone flow.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <AppButton
                  tone={googleCalendarReady ? 'primary' : 'outline'}
                  onPress={() => void handleCalendarModeChange(googleCalendarReady ? 'adulting' : 'google_calendar')}
                  disabled={calendarModeSaving}
                  className="w-full"
                >
                  {calendarModeSaving ? 'Working…' : googleCalendarReady ? 'Use Adulting calendar instead' : 'Connect Google Calendar'}
                </AppButton>
                <AppButton
                  tone="outline"
                  onPress={() => void handleCalendarModeChange('adulting')}
                  disabled={calendarModeSaving}
                  className="w-full"
                >
                  Keep Adulting calendar
                </AppButton>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">Phone reminders</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Turn these on in the iPhone or Android app if you want Adulting reminders and community alerts in your phone notification centre. The web app cannot register phone push on its own.
            </p>
          </div>

          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <p className="text-sm font-semibold text-foreground">{SETUP_CHOICES_TITLE}</p>
            <p className="text-xs leading-relaxed text-muted-foreground">{SETUP_CHOICES_SUBTITLE}</p>
            <div className="space-y-3">
              {setupChoices.map((choice) => (
                <div key={choice.label} className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{choice.label}</p>
                  <p className="text-xs leading-relaxed text-muted-foreground">{choice.body}</p>
                </div>
              ))}
            </div>
          </div>

          {selectedSchoolNames.length ? (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">School dates</p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Adulting has school selections for {selectedSchoolNames.join(', ')}. If you want, import the schools’ own published term dates now so Family starts with the right dates already in place.
              </p>
              <AppButton onPress={() => void handleImportSchoolDates()} disabled={schoolCalendarImportBusy} className="w-full">
                {schoolCalendarImportBusy ? 'Importing…' : 'Import school dates'}
              </AppButton>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-2xl animate-fade-in">
        <div className="rounded-[28px] border border-card-border bg-card px-5 py-6 shadow-sm mb-6">
          <img src="/adulting-logo.png" alt="Adulting" className="h-16 mx-auto mb-4" />
          <div className="space-y-4 text-left">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-primary">{ONBOARDING_WELCOME_TITLE}</h1>
            </div>
            <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
              <p>{ONBOARDING_WELCOME_BODY_PRIMARY}</p>
              <p>{ONBOARDING_WELCOME_BODY_SECONDARY}</p>
            </div>
            <div className="rounded-2xl bg-muted/50 px-4 py-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">Setup progress</p>
                <p className="text-xs text-muted-foreground">Step {step} of {ONBOARDING_STEP_SEQUENCE.length}</p>
              </div>
              <div className="grid gap-2 sm:grid-cols-5">
                {ONBOARDING_STEP_SEQUENCE.map((item) => {
                  const isCurrent = item.step === step;
                  const isComplete = item.step < step;

                  return (
                    <div
                      key={item.step}
                      className={`rounded-2xl border px-3 py-2 text-left ${
                        isCurrent
                          ? 'border-primary bg-primary/5'
                          : isComplete
                            ? 'border-primary/30 bg-primary/5'
                            : 'border-border bg-card'
                      }`}
                    >
                      <p className="text-xs font-semibold text-foreground">{item.label}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{item.hint}</p>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">{ONBOARDING_STEP_COPY[step].title}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{ONBOARDING_STEP_COPY[step].body}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{ONBOARDING_STEP_SUPPORT_NOTE}</p>
              </div>
            </div>
          </div>
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 grid gap-3 sm:grid-cols-2">
              <AppInput
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First name"
                autoComplete="given-name"
                spellCheck={false}
              />
              <UkDateField
                label="Date of birth"
                value={dateOfBirth}
                onChange={setDateOfBirth}
                blankHint="Add your date of birth now or come back in My Settings later."
              />
            </div>

            <div className="space-y-3">
              {ONBOARDING_WORK_OPTIONS.map((option) => (
                <AppPanelButton
                  key={option.value}
                  onClick={() => setWorkType(option.value as WorkType)}
                  className={`gap-3 rounded-xl border-2 p-4 ${
                    workType === option.value
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <div className="min-w-0 text-left">
                    <span className="block text-sm font-medium text-foreground">{option.label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{option.note}</span>
                  </div>
                </AppPanelButton>
              ))}
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              {ONBOARDING_DATA_PROMISE}
            </div>

            <AppButton onPress={goNext} disabled={!canAdvance[1]} className="w-full">
              Next
            </AppButton>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { value: 'yes', emoji: '💛', label: 'Yes - I have a partner or shared household' },
                { value: 'no', emoji: '🙋', label: 'No - it is just me on that side' },
              ].map((option) => (
                <AppPanelButton
                  key={option.value}
                  onClick={() => setPartnerChoice(option.value as OnboardingYesNo)}
                  className={`gap-3 rounded-xl border-2 p-4 ${
                    partnerChoice === option.value
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <span className="text-sm font-medium text-foreground">{option.label}</span>
                </AppPanelButton>
              ))}
            </div>

            {partnerChoice === 'yes' ? (
              <div className="rounded-2xl border border-border bg-card p-4 grid gap-3 sm:grid-cols-2">
                <AppInput
                  value={partnerName}
                  onChange={(event) => setPartnerName(event.target.value)}
                  placeholder="Partner name"
                  spellCheck={false}
                />
                <UkDateField
                  label="Partner date of birth"
                  value={partnerDateOfBirth}
                  onChange={setPartnerDateOfBirth}
                  blankHint="Add your partner's date of birth now or fill it in later in My Settings."
                />
              </div>
            ) : null}

            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Dependants</p>
              <div className="flex flex-wrap gap-2">
                {ONBOARDING_DEPENDANT_OPTIONS.map((option) => {
                  const selected = dependantTypes.includes(option.value as DependantType);
                  return (
                    <AppButton
                      key={option.value}
                      tone={selected ? 'primary' : 'outline'}
                      size="sm"
                      onPress={() => toggleDependant(option.value as DependantType)}
                    >
                      <span className="mr-1.5">{option.emoji}</span>
                      {option.label}
                    </AppButton>
                  );
                })}
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Add names and dates now if you want Adulting to be more ready from the start, or leave them blank and fill them in later in My Settings.
              </p>
            </div>

            {dependantTypes.includes('children') ? (
              <div className="space-y-3">
                {childDrafts.map((child) => (
                  <div key={child.id} className="rounded-2xl border border-border bg-card p-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <AppInput
                        value={child.name}
                        onChange={(event) => setChildDraft(child.id, 'name', event.target.value)}
                        placeholder="Child name"
                        spellCheck={false}
                      />
                      <UkDateField
                        label="Date of birth"
                        value={child.dateOfBirth}
                        onChange={(value) => setChildDraft(child.id, 'dateOfBirth', value)}
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <AppTextLinkButton onClick={() => removeChildDraft(child.id)} className="text-xs text-muted-foreground no-underline hover:no-underline">
                        Remove
                      </AppTextLinkButton>
                    </div>
                  </div>
                ))}
                <AppButton tone="outline" onPress={addChildDraft} className="w-full">
                  Add another child
                </AppButton>
              </div>
            ) : null}

            {dependantTypes.some((type) => type === 'elderly' || type === 'disabled') ? (
              <div className="space-y-3">
                {adultDependantDrafts
                  .filter((dependant) => dependantTypes.includes(dependant.kind))
                  .map((dependant) => (
                    <div key={dependant.id} className="rounded-2xl border border-border bg-card p-4">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <AppInput
                          value={dependant.name}
                          onChange={(event) => setAdultDependantDraft(dependant.id, 'name', event.target.value)}
                          placeholder="Dependant name"
                          spellCheck={false}
                        />
                        <UkDateField
                          label="Date of birth"
                          value={dependant.dateOfBirth}
                          onChange={(value) => setAdultDependantDraft(dependant.id, 'dateOfBirth', value)}
                        />
                        <AppNativeSelect value={dependant.kind} onChange={(event) => setAdultDependantDraft(dependant.id, 'kind', event.target.value)}>
                          <option value="elderly">Older family member</option>
                          <option value="disabled">Disabled or unwell family member</option>
                        </AppNativeSelect>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <AppTextLinkButton onClick={() => removeAdultDependantDraft(dependant.id)} className="text-xs text-muted-foreground no-underline hover:no-underline">
                          Remove
                        </AppTextLinkButton>
                      </div>
                    </div>
                  ))}
                <AppButton tone="outline" onPress={addAdultDependantDraft} className="w-full">
                  Add another dependant
                </AppButton>
              </div>
            ) : null}

            <AppButton onPress={goNext} disabled={!canAdvance[2]} className="w-full">
              Next
            </AppButton>
            <AppTextLinkButton onClick={goBack} className="w-full py-2 text-center text-xs text-muted-foreground no-underline hover:no-underline">
              ← Back
            </AppTextLinkButton>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { value: 'no', emoji: '🙋', label: 'No pets to track' },
                { value: 'yes', emoji: '🐾', label: 'Yes - add pets' },
              ].map((option) => (
                <AppPanelButton
                  key={option.value}
                  onClick={() => setHasPets(option.value as OnboardingYesNo)}
                  className={`gap-3 rounded-xl border-2 p-4 ${
                    hasPets === option.value
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <span className="text-sm font-medium text-foreground">{option.label}</span>
                </AppPanelButton>
              ))}
            </div>

            {hasPets === 'yes' ? (
              <div className="space-y-3">
                {petDrafts.map((pet) => (
                  <div key={pet.id} className="rounded-2xl border border-border bg-card p-4">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <AppInput
                        value={pet.name}
                        onChange={(event) => setPetDraft(pet.id, 'name', event.target.value)}
                        placeholder="Pet name"
                        spellCheck={false}
                      />
                      <AppNativeSelect value={pet.petType} onChange={(event) => setPetDraft(pet.id, 'petType', event.target.value)}>
                        {HEALTH_PET_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {formatPetTypeLabel(option)}
                          </option>
                        ))}
                      </AppNativeSelect>
                      <UkDateField
                        label="Date of birth"
                        value={pet.dateOfBirth}
                        onChange={(value) => setPetDraft(pet.id, 'dateOfBirth', value)}
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <AppTextLinkButton onClick={() => removePetDraft(pet.id)} className="text-xs text-muted-foreground no-underline hover:no-underline">
                        Remove
                      </AppTextLinkButton>
                    </div>
                  </div>
                ))}
                <AppButton tone="outline" onPress={addPetDraft} className="w-full">
                  Add another pet
                </AppButton>
              </div>
            ) : null}

            <AppButton onPress={goNext} disabled={!canAdvance[3]} className="w-full">
              Next
            </AppButton>
            <AppTextLinkButton onClick={goBack} className="w-full py-2 text-center text-xs text-muted-foreground no-underline hover:no-underline">
              ← Back
            </AppTextLinkButton>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 grid gap-3 sm:grid-cols-2">
              <AppInput
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                placeholder="Home address"
                autoComplete="street-address"
              />
              <AppNativeSelect value={tenure} onChange={(event) => setTenure(event.target.value)}>
                <option value="">Choose your housing situation</option>
                {ONBOARDING_TENURE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </AppNativeSelect>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              A full address with postcode helps Adulting confirm nearby schools and pull in the right home context without another setup hunt later.
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <p className="text-sm font-semibold text-foreground">Council tax band</p>
              <div className="flex flex-wrap gap-2">
                {ONBOARDING_COUNCIL_TAX_BANDS.map((band) => (
                  <AppButton
                    key={band}
                    tone={councilTaxBand === band ? 'primary' : 'outline'}
                    size="sm"
                    onPress={() => setCouncilTaxBand(councilTaxBand === band ? '' : band)}
                  >
                    Band {band}
                  </AppButton>
                ))}
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                If you do not know it yet, leave it unset and add it later in My Settings.
              </p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-semibold text-foreground">Home routines</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{ONBOARDING_HOME_ROUTINES_NOTE}</p>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Lawn</p>
                  <div className="flex flex-wrap gap-2">
                    {([
                      ['yes', 'Yes'],
                      ['no', 'No'],
                      ['', 'Do later'],
                    ] as const).map(([value, label]) => (
                      <AppButton
                        key={`lawn-${label}`}
                        type="button"
                        tone={hasLawn === value ? 'primary' : 'outline'}
                        size="sm"
                        onPress={() => setHasLawn(value)}
                      >
                        {label}
                      </AppButton>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Houseplants</p>
                  <div className="flex flex-wrap gap-2">
                    {([
                      ['yes', 'Yes'],
                      ['no', 'No'],
                      ['', 'Do later'],
                    ] as const).map(([value, label]) => (
                      <AppButton
                        key={`houseplants-${label}`}
                        type="button"
                        tone={hasHouseplants === value ? 'primary' : 'outline'}
                        size="sm"
                        onPress={() => setHasHouseplants(value)}
                      >
                        {label}
                      </AppButton>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                { value: 'yes', emoji: '🚗', label: 'Yes - there is a vehicle to track' },
                { value: 'no', emoji: '🏡', label: 'No vehicle to track' },
              ].map((option) => (
                <AppPanelButton
                  key={option.value}
                  onClick={() => setHasVehicle(option.value as OnboardingYesNo)}
                  className={`gap-3 rounded-xl border-2 p-4 ${
                    hasVehicle === option.value
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  }`}
                >
                  <span className="text-2xl">{option.emoji}</span>
                  <span className="text-sm font-medium text-foreground">{option.label}</span>
                </AppPanelButton>
              ))}
            </div>

            {hasVehicle === 'yes' ? (
              <div className="rounded-2xl border border-border bg-card p-4">
                <AppInput
                  value={vehicleRegistrationNumber}
                  onChange={(event) => setVehicleRegistrationNumber(event.target.value.toUpperCase())}
                  placeholder="Vehicle registration"
                  autoCapitalize="characters"
                  spellCheck={false}
                />
              </div>
            ) : null}

            {schoolEligibleChildren.length ? (
              <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">School confirmation</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    For children under 18, Adulting will look up nearby schools as soon as your home address includes a postcode, let you confirm the right one for each child, then offer the school-date import once setup is saved.
                  </p>
                </div>
                <AppButton tone="outline" onClick={() => void handleLookupChildSchools()} disabled={childSchoolLookupBusy} className="w-full">
                  {childSchoolLookupBusy ? 'Looking up nearby schools…' : childSchoolCandidates.length ? 'Refresh nearby schools' : 'Find nearby schools'}
                </AppButton>
                {childSchoolLookupError ? (
                  <p className="text-xs leading-relaxed text-muted-foreground">{childSchoolLookupError}</p>
                ) : null}
                {childSchoolCandidates.length ? (
                  <div className="space-y-3">
                    {schoolEligibleChildren.map((child) => (
                      <div key={child.id} className="rounded-2xl border border-border bg-background px-4 py-3 space-y-2">
                        <p className="text-sm font-medium text-foreground">{child.name.trim() || 'This child'}</p>
                        <AppNativeSelect
                          value={child.schoolUrn}
                          onChange={(event) => assignChildSchool(child.id, event.target.value)}
                        >
                          <option value="">Select their school</option>
                          {childSchoolCandidates.map((school) => (
                            <option key={school.urn} value={school.urn}>
                              {school.town ? `${school.name} · ${school.town}` : school.name}
                            </option>
                          ))}
                        </AppNativeSelect>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            <AppButton onPress={goNext} disabled={!canAdvance[4]} className="w-full">
              Next
            </AppButton>
            <AppTextLinkButton onClick={goBack} className="w-full py-2 text-center text-xs text-muted-foreground no-underline hover:no-underline">
              ← Back
            </AppTextLinkButton>
          </div>
        ) : null}

        {step === 5 ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 text-xs leading-relaxed text-muted-foreground">
              {ONBOARDING_CONNECTIONS_NOTE}
            </div>
            {[
              {
                title: 'Banking',
                helper: 'Add the basics manually if you want control, connect banking later if you want speed, or use both together for the fullest picture.',
                value: bankIntent,
                onChange: setBankIntent,
              },
              {
                title: 'Documents',
                helper: 'Let Adulting keep files securely in sync across your Adulting devices, connect your own storage later, or use a mix of both.',
                value: storageIntent,
                onChange: setStorageIntent,
              },
              {
                title: 'Calendar',
                helper: 'Let Adulting keep the source calendar inside Adulting, connect your own calendar later, or use both together.',
                value: calendarIntent,
                onChange: setCalendarIntent,
              },
            ].map((group) => (
              <div key={group.title} className="rounded-2xl border border-border bg-card p-4 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{group.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{group.helper}</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {ONBOARDING_INTEGRATION_INTENT_OPTIONS.map((option) => (
                    <AppPanelButton
                      key={option.value}
                      onClick={() => group.onChange(option.value)}
                      className={`items-start rounded-xl border-2 p-4 text-left ${
                        group.value === option.value
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-muted-foreground/30'
                      }`}
                    >
                      <span className="text-sm font-medium text-foreground">{option.label}</span>
                      <span className="mt-1 text-xs leading-relaxed text-muted-foreground">{option.note}</span>
                    </AppPanelButton>
                  ))}
                </div>
              </div>
            ))}

            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-foreground">Phone reminders</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  On iPhone or Android, turn these on later if you want Adulting to appear in your phone notification centre. The web app cannot register phone push.
                </p>
              </div>
            </div>

            <AppButton onPress={() => void handleFinish()} disabled={finishing || !canAdvance[5]} className="w-full">
              {finishing ? 'Getting Adulting ready…' : 'Save and let Adulting get ready'}
            </AppButton>
            <AppTextLinkButton onClick={goBack} className="w-full py-2 text-center text-xs text-muted-foreground no-underline hover:no-underline">
              ← Back
            </AppTextLinkButton>
          </div>
        ) : null}

        <AppTextLinkButton
          onClick={handleSkip}
          className="mt-2 w-full py-3 text-center text-xs text-muted-foreground no-underline hover:text-foreground hover:no-underline"
        >
          Skip for now
        </AppTextLinkButton>
      </div>
    </div>
  );
}
